import { useSeoMeta } from '@unhead/react';
import { useState } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { LoginArea } from '@/components/auth/LoginArea';
import { useNostr } from '@nostrify/react';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, subDays, parseISO } from 'date-fns';
import { Plus, Edit2, Trash2, Sun, Moon, ArrowUp, ArrowDown, Archive, ArchiveRestore } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useTheme } from '@/hooks/useTheme';
import { useAppContext } from '@/hooks/useAppContext';
import type { ColorTheme } from '@/contexts/AppContext';

// Nostr event kinds (addressable for plans, regular for logs)
const TRAINING_PLAN_KIND = 30090;
const BODY_METRIC_KIND = 30091;
const NUTRITION_KIND = 30092;

interface TrainingPlan {
  id: string;
  name: string;
  description: string;
  exercises: Array<{ name: string; sets: number; reps: number; weight?: number; rest?: number }>;
  frequency: string;
  goal: string;
  createdAt: string;
  archived?: boolean;
}

interface BodyMetric {
  date: string;
  weight: number;
  bodyFat: number;
  muscle: number;
  visceralFat: number;
}

interface NutritionEntry {
  date: string;
  food: string;
  amount: number;
  unit: string;
  calories?: number;
}

function ComparisonResults({ date1, date2, metrics }: { date1: Date; date2: Date; metrics: BodyMetric[] }) {
  const d1 = format(date1, 'yyyy-MM-dd');
  const d2 = format(date2, 'yyyy-MM-dd');

  // Find exact or closest data
  const findClosestMetric = (targetDate: string) => {
    const exact = metrics.find(m => m.date === targetDate);
    if (exact) return { metric: exact, note: null };

    // Find closest previous or next
    const sorted = [...metrics].sort((a, b) => a.date.localeCompare(b.date));
    let closest = sorted[0];
    let minDiff = Infinity;

    for (const m of sorted) {
      const diff = Math.abs(new Date(m.date).getTime() - new Date(targetDate).getTime());
      if (diff < minDiff) {
        minDiff = diff;
        closest = m;
      }
    }

    const note = closest.date !== targetDate 
      ? `Using data from ${closest.date} (closest available)` 
      : null;

    return { metric: closest, note };
  };

  const result1 = findClosestMetric(d1);
  const result2 = findClosestMetric(d2);

  const m1 = result1.metric;
  const m2 = result2.metric;

  if (!m1 || !m2) {
    return <p className="text-sm text-muted-foreground">No data available for comparison.</p>;
  }

  const weightDiff = m2.weight - m1.weight;
  const weightPct = m1.weight ? ((weightDiff / m1.weight) * 100) : 0;

  const bodyFatKg1 = Math.round((m1.weight * (m1.bodyFat / 100)) * 10) / 10;
  const bodyFatKg2 = Math.round((m2.weight * (m2.bodyFat / 100)) * 10) / 10;
  const bodyFatKgDiff = bodyFatKg2 - bodyFatKg1;
  const bodyFatKgPct = bodyFatKg1 ? ((bodyFatKgDiff / bodyFatKg1) * 100) : 0;

  const musclePct1 = Math.round(((m1.muscle / m1.weight) * 100) * 10) / 10;
  const musclePct2 = Math.round(((m2.muscle / m2.weight) * 100) * 10) / 10;
  const musclePctDiff = musclePct2 - musclePct1;
  const musclePctPct = musclePct1 ? ((musclePctDiff / musclePct1) * 100) : 0;

  const muscleKgDiff = m2.muscle - m1.muscle;
  const muscleKgPct = m1.muscle ? ((muscleKgDiff / m1.muscle) * 100) : 0;

  const visceralDiff = m2.visceralFat - m1.visceralFat;
  const visceralPct = m1.visceralFat ? ((visceralDiff / m1.visceralFat) * 100) : 0;

  const rows = [
    { name: 'Weight', unit: 'kg', v1: m1.weight, v2: m2.weight, diff: weightDiff, pct: weightPct },
    { name: 'Body Fat', unit: '%', v1: m1.bodyFat, v2: m2.bodyFat, diff: m2.bodyFat - m1.bodyFat, pct: m1.bodyFat ? (((m2.bodyFat - m1.bodyFat) / m1.bodyFat) * 100) : 0 },
    { name: 'Body Fat', unit: 'kg', v1: bodyFatKg1, v2: bodyFatKg2, diff: bodyFatKgDiff, pct: bodyFatKgPct },
    { name: 'Muscle', unit: 'kg', v1: m1.muscle, v2: m2.muscle, diff: muscleKgDiff, pct: muscleKgPct },
    { name: 'Muscle', unit: '%', v1: musclePct1, v2: musclePct2, diff: musclePctDiff, pct: musclePctPct },
    { name: 'Visceral Fat', unit: '%', v1: m1.visceralFat, v2: m2.visceralFat, diff: visceralDiff, pct: visceralPct },
  ];

  return (
    <div className="space-y-3 text-sm">
      {/* Notes about closest data */}
      {(result1.note || result2.note) && (
        <div className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/30 p-2 rounded">
          {result1.note && <div>{result1.note}</div>}
          {result2.note && <div>{result2.note}</div>}
        </div>
      )}

      {rows.map((row, index) => (
        <div key={index} className="flex justify-between border-b pb-1">
          <span className="font-medium">{row.name} ({row.unit})</span>
          <span className="text-right">
            {row.v1} → {row.v2}{' '}
            <span className={row.diff >= 0 ? 'text-green-600' : 'text-red-600'}>
              ({row.diff >= 0 ? '+' : ''}{row.diff.toFixed(1)} / {row.pct >= 0 ? '+' : ''}{row.pct.toFixed(1)}%)
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

function ThemeToggle() {
  const { theme, setTheme } = useTheme();

  const toggleTheme = () => {
    if (theme === 'light') {
      setTheme('dark');
    } else {
      setTheme('light');
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggleTheme}
      aria-label="Toggle theme"
    >
      {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
    </Button>
  );
}

function ColorThemeSelector() {
  const { config, updateConfig } = useAppContext();

  const setColorTheme = (newTheme: ColorTheme) => {
    updateConfig(() => ({ colorTheme: newTheme }));
  };

  return (
    <Select value={config.colorTheme} onValueChange={setColorTheme}>
      <SelectTrigger className="w-[140px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="default">Default</SelectItem>
        <SelectItem value="cyberpunk">Cyberpunk Neon</SelectItem>
      </SelectContent>
    </Select>
  );
}

const Index = () => {
  useSeoMeta({
    title: 'Training & Body Tracker • Nostr',
    description: 'Track training plans, body metrics, and nutrition on Nostr. Private, portable, and yours forever.',
  });

  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const { mutate: publish } = useNostrPublish();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<'training' | 'body' | 'nutrition'>('training');

  // Training state
  const [plans, setPlans] = useState<TrainingPlan[]>([]);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState<TrainingPlan | null>(null);
  const [expandedExerciseIndex, setExpandedExerciseIndex] = useState<number | null>(null);
  const [planForm, setPlanForm] = useState({
    name: '', description: '', frequency: '', goal: '',
    exercises: [{ name: '', sets: 3, reps: 10, weight: 0, rest: 60 }]
  });

  // Computed plan lists
  const activePlans = plans.filter(p => !p.archived);
  const archivedPlans = plans.filter(p => p.archived);

  // Body Tracking state
  const [bodyMetrics, setBodyMetrics] = useState<BodyMetric[]>([]);
  const [bodyForm, setBodyForm] = useState({ weight: 80, bodyFat: 15, musclePct: 40, visceralFat: 10 });
  const [weightInput, setWeightInput] = useState('80.0');
  const [bodyFatInput, setBodyFatInput] = useState('15.0');
  const [musclePctInput, setMusclePctInput] = useState('40.0');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // Dates that already have body metrics logged
  const loggedDates = bodyMetrics.map(m => parseISO(m.date));
  const [chartSeries, setChartSeries] = useState<string[]>(['weight', 'bodyFatPct', 'muscleKg', 'visceral']);
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: subDays(new Date(), 90),
    to: new Date(),
  });

  // Date comparison feature
  const [compareDate1, setCompareDate1] = useState<Date | null>(null);
  const [compareDate2, setCompareDate2] = useState<Date | null>(null);

  // Nutrition state
  const [nutritionEntries, setNutritionEntries] = useState<NutritionEntry[]>([]);
  const [nutritionForm, setNutritionForm] = useState({ food: '', amount: 100, unit: 'g' });
  const [nutritionDate, setNutritionDate] = useState<Date>(new Date());
  const [showClearNutritionDialog, setShowClearNutritionDialog] = useState(false);

  // Dates that have at least one nutrition entry (for calendar highlighting)
  const nutritionLoggedDates = Array.from(
    new Set(nutritionEntries.map(e => e.date))
  ).map(dateStr => parseISO(dateStr));

  // Store original values when a date is selected (for Undo)
  const [originalMetric, setOriginalMetric] = useState<BodyMetric | null>(null);
  const [showClearAllDialog, setShowClearAllDialog] = useState(false);

  // Prefill form when selected date changes
  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;
    setSelectedDate(date);

    const dateStr = format(date, 'yyyy-MM-dd');
    const existing = bodyMetrics.find(m => m.date === dateStr);

    if (existing) {
      setOriginalMetric(existing);
      // Prefill with existing data
      setBodyForm({
        weight: existing.weight,
        bodyFat: existing.bodyFat,
        musclePct: Math.round(((existing.muscle / existing.weight) * 100) * 10) / 10,
        visceralFat: existing.visceralFat,
      });
      setWeightInput(existing.weight.toFixed(1));
      setBodyFatInput(existing.bodyFat.toFixed(1));
      setMusclePctInput(((existing.muscle / existing.weight) * 100).toFixed(1));
    } else {
      setOriginalMetric(null);
      // No data for this date → show blank fields
      setBodyForm({ weight: 0, bodyFat: 0, musclePct: 0, visceralFat: 0 });
      setWeightInput('');
      setBodyFatInput('');
      setMusclePctInput('');
    }
  };

  // Fetch data from Nostr (runs when user logs in)
  const { data: nostrData, isLoading } = useQuery({
    queryKey: ['tracker-data', user?.pubkey],
    queryFn: async () => {
      if (!user) return { plans: [], body: [], nutrition: [] };

      const since = Math.floor(Date.now() / 1000) - 60 * 60 * 24 * 365; // 1 year

      const events = await nostr.query([
        { kinds: [TRAINING_PLAN_KIND], authors: [user.pubkey], limit: 100 },
        { kinds: [BODY_METRIC_KIND], authors: [user.pubkey], since, limit: 500 },
        { kinds: [NUTRITION_KIND], authors: [user.pubkey], since, limit: 500 },
      ]);

      const parsedPlans: TrainingPlan[] = [];
      const parsedBody: BodyMetric[] = [];
      const parsedNutrition: NutritionEntry[] = [];

      for (const ev of events) {
        try {
          if (ev.kind === TRAINING_PLAN_KIND) {
            const d = ev.tags.find(t => t[0] === 'd')?.[1];
            if (d && ev.content) {
              const data = JSON.parse(ev.content);
              parsedPlans.push({ id: d, ...data, createdAt: new Date(ev.created_at * 1000).toISOString() });
            }
          } else if (ev.kind === BODY_METRIC_KIND) {
            const data = JSON.parse(ev.content);
            parsedBody.push(data);
          } else if (ev.kind === NUTRITION_KIND) {
            const data = JSON.parse(ev.content);
            parsedNutrition.push(data);
          }
        } catch (_) { /* ignore malformed */ }
      }

      // Sort
      parsedBody.sort((a, b) => a.date.localeCompare(b.date));
      parsedNutrition.sort((a, b) => a.date.localeCompare(b.date));

      setPlans(parsedPlans);
      setBodyMetrics(parsedBody);

      // Merge with existing local state to protect newly added entries
      setNutritionEntries(prev => {
        const existingDates = new Set(prev.map(e => e.date + e.food + e.amount));
        const newOnes = parsedNutrition.filter(m => !existingDates.has(m.date + m.food + m.amount));
        return [...prev, ...newOnes].sort((a, b) => a.date.localeCompare(b.date));
      });

      return { plans: parsedPlans, body: parsedBody, nutrition: parsedNutrition };
    },
    enabled: !!user,
  });

  // Training Plan handlers
  const saveTrainingPlan = async () => {
    if (!user) return;

    const planId = editingPlan?.id || `plan-${Date.now()}`;
    const eventContent = JSON.stringify({
      name: planForm.name,
      description: planForm.description,
      exercises: planForm.exercises,
      frequency: planForm.frequency,
      goal: planForm.goal,
      archived: editingPlan?.archived ?? false,
    });

    publish({
      kind: TRAINING_PLAN_KIND,
      content: eventContent,
      tags: [['d', planId]],
    });

    // Optimistic update
    const newPlan: TrainingPlan = {
      id: planId,
      ...JSON.parse(eventContent),
      createdAt: editingPlan?.createdAt || new Date().toISOString(),
      archived: editingPlan?.archived ?? false,
    };

    // Use functional update to avoid stale state issues
    setPlans(prev => {
      if (editingPlan) {
        return prev.map(p => p.id === planId ? newPlan : p);
      } else {
        return [...prev, newPlan];
      }
    });

    setShowPlanDialog(false);
    setEditingPlan(null);
    resetPlanForm();
    // Do NOT invalidate — prevents previously deleted plans from reappearing
  };

  const deleteTrainingPlan = (id: string) => {
    setPlans(plans.filter(p => p.id !== id));
    // Do not invalidate — prevents deleted plans from reappearing
  };

  const archivePlan = (id: string) => {
    setPlans(prev =>
      prev.map(p => {
        if (p.id === id) {
          const updated = { ...p, archived: true };
          // Re-publish the updated plan
          publish({
            kind: TRAINING_PLAN_KIND,
            content: JSON.stringify({
              name: updated.name,
              description: updated.description,
              exercises: updated.exercises,
              frequency: updated.frequency,
              goal: updated.goal,
              archived: true,
            }),
            tags: [['d', id]],
          });
          return updated;
        }
        return p;
      })
    );
  };

  const unarchivePlan = (id: string) => {
    setPlans(prev =>
      prev.map(p => {
        if (p.id === id) {
          const updated = { ...p, archived: false };
          publish({
            kind: TRAINING_PLAN_KIND,
            content: JSON.stringify({
              name: updated.name,
              description: updated.description,
              exercises: updated.exercises,
              frequency: updated.frequency,
              goal: updated.goal,
              archived: false,
            }),
            tags: [['d', id]],
          });
          return updated;
        }
        return p;
      })
    );
  };

  const editTrainingPlan = (plan: TrainingPlan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name,
      description: plan.description,
      frequency: plan.frequency,
      goal: plan.goal,
      exercises: plan.exercises.length ? plan.exercises : [{ name: '', sets: 3, reps: 10 }],
    });
    setShowPlanDialog(true);
  };

  const resetPlanForm = () => {
    setPlanForm({
      name: '', description: '', frequency: '', goal: '',
      exercises: [{ name: '', sets: 3, reps: 10, weight: 0, rest: 60 }],
    });
  };

  const addExercise = () => {
    setPlanForm({
      ...planForm,
      exercises: [...planForm.exercises, { name: '', sets: 3, reps: 10, weight: 0, rest: 60 }],
    });
  };

  // Body Tracking handlers
  const saveBodyMetric = () => {
    if (!user) return;

    // Convert muscle percentage to absolute kg for storage
    const muscleKg = Math.round((bodyForm.weight * (bodyForm.musclePct / 100)) * 10) / 10;

    const metric: BodyMetric = {
      date: format(selectedDate, 'yyyy-MM-dd'),
      weight: Math.round(bodyForm.weight * 10) / 10,
      bodyFat: Math.round(bodyForm.bodyFat * 10) / 10,
      muscle: muscleKg,           // store absolute value
      visceralFat: bodyForm.visceralFat,
    };

    const content = JSON.stringify(metric);

    publish({
      kind: BODY_METRIC_KIND,
      content,
      tags: [['d', `body-${metric.date}`], ['t', 'body-metric']],
    });

    const exists = bodyMetrics.findIndex(m => m.date === metric.date);
    const updated = exists >= 0
      ? bodyMetrics.map((m, i) => i === exists ? metric : m)
      : [...bodyMetrics, metric].sort((a, b) => a.date.localeCompare(b.date));

    setBodyMetrics(updated);
    // Do not invalidate — we manage bodyMetrics locally after user clears data
  };

  // Filtered + toggled chart data with both % and absolute values
  const filteredBodyData = bodyMetrics
    .filter(m => {
      const d = parseISO(m.date);
      return d >= dateRange.from && d <= dateRange.to;
    })
    .map(m => {
      const bodyFatKg = Math.round((m.weight * (m.bodyFat / 100)) * 10) / 10;
      const musclePct = Math.round(((m.muscle / m.weight) * 100) * 10) / 10;

      return {
        date: m.date,
        weight: m.weight,
        bodyFatPct: m.bodyFat,
        bodyFatKg,
        muscleKg: m.muscle,
        musclePct,
        visceral: m.visceralFat,
      };
    });

  const toggleSeries = (key: string) => {
    setChartSeries(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Nutrition handlers
  const saveNutrition = () => {
    if (!user) return;

    // Simple auto unit detection
    let unit = nutritionForm.unit;
    const foodLower = nutritionForm.food.toLowerCase();
    if (['milk', 'water', 'juice', 'coffee'].some(w => foodLower.includes(w)) && unit === 'g') unit = 'ml';

    const entry: NutritionEntry = {
      date: format(nutritionDate, 'yyyy-MM-dd'),
      food: nutritionForm.food,
      amount: nutritionForm.amount,
      unit,
    };

    publish({
      kind: NUTRITION_KIND,
      content: JSON.stringify(entry),
      tags: [['t', 'nutrition']],
    });

    // Use functional update to avoid stale closure issues
    setNutritionEntries(prev =>
      [...prev, entry].sort((a, b) => a.date.localeCompare(b.date))
    );

    setNutritionForm({ food: '', amount: 100, unit: 'g' });
    // Do not invalidate — we manage nutrition locally to avoid overrides
  };

  const nutritionForDate = nutritionEntries.filter(e => e.date === format(nutritionDate, 'yyyy-MM-dd'));

  // ==================== EXPORT / IMPORT ====================

  const exportTrainingPlans = () => {
    const data = {
      type: 'training-plans',
      exportedAt: new Date().toISOString(),
      plans: plans,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `training-plans-${format(new Date(), 'yyyy-MM-dd')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const importTrainingPlans = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target?.result as string);
        if (imported.type === 'training-plans' && Array.isArray(imported.plans)) {
          const newPlans = [...plans];
          let added = 0;

          imported.plans.forEach((p: any) => {
            if (!newPlans.some(existing => existing.id === p.id)) {
              newPlans.push(p);
              added++;
            }
          });

          setPlans(newPlans);
          alert(`Imported ${added} new training plan(s).`);
        } else {
          alert('Invalid training plans file.');
        }
      } catch (err) {
        alert('Failed to parse the file.');
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // reset input
  };

  const exportBodyMetrics = () => {
    const data = {
      type: 'body-metrics',
      exportedAt: new Date().toISOString(),
      metrics: bodyMetrics,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `body-metrics-${format(new Date(), 'yyyy-MM-dd')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const importBodyMetrics = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target?.result as string);
        if (imported.type === 'body-metrics' && Array.isArray(imported.metrics)) {
          const newMetrics = [...bodyMetrics];
          let added = 0;

          imported.metrics.forEach((m: BodyMetric) => {
            if (!newMetrics.some(existing => existing.date === m.date)) {
              newMetrics.push(m);
              added++;
            }
          });

          newMetrics.sort((a, b) => a.date.localeCompare(b.date));
          setBodyMetrics(newMetrics);
          alert(`Imported ${added} new body metric entry/entries.`);
        } else {
          alert('Invalid body metrics file.');
        }
      } catch (err) {
        alert('Failed to parse the file.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Remove a single nutrition entry
  const removeNutritionEntry = (indexToRemove: number) => {
    setNutritionEntries(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  // Clear all nutrition entries
  const clearAllNutrition = () => {
    setNutritionEntries([]);
    setShowClearNutritionDialog(false);
  };

  // Clear nutrition entries for a specific day
  const clearNutritionForDay = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    setNutritionEntries(prev => prev.filter(entry => entry.date !== dateStr));
  };

  // Export Nutrition data
  const exportNutrition = () => {
    const data = {
      type: 'nutrition',
      exportedAt: new Date().toISOString(),
      entries: nutritionEntries,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nutrition-${format(new Date(), 'yyyy-MM-dd')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Import Nutrition data
  const importNutrition = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target?.result as string);
        if (imported.type === 'nutrition' && Array.isArray(imported.entries)) {
          const newEntries = [...nutritionEntries];
          let added = 0;

          imported.entries.forEach((entry: NutritionEntry) => {
            // Simple duplicate check: same date + food + amount + unit
            const isDuplicate = newEntries.some(e =>
              e.date === entry.date &&
              e.food === entry.food &&
              e.amount === entry.amount &&
              e.unit === entry.unit
            );
            if (!isDuplicate) {
              newEntries.push(entry);
              added++;
            }
          });

          newEntries.sort((a, b) => a.date.localeCompare(b.date));
          setNutritionEntries(newEntries);
          alert(`Imported ${added} new nutrition entry/entries.`);
        } else {
          alert('Invalid nutrition file.');
        }
      } catch (err) {
        alert('Failed to parse the file.');
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // reset input
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="font-bold text-2xl tracking-tight">TrainTrack</div>
            <Badge variant="secondary">Nostr</Badge>
          </div>
          <div className="flex items-center gap-4">
            <ColorThemeSelector />
            <ThemeToggle />
            <LoginArea className="max-w-60" />
            <a href="https://shakespeare.diy" target="_blank" className="text-sm text-muted-foreground hover:text-foreground">
              Vibed with Shakespeare
            </a>
          </div>
        </div>
      </header>

      <main className="container py-8">
        {!user ? (
          <div className="text-center py-20">
            <h1 className="text-5xl font-bold mb-4">Your Personal Training &amp; Body Tracker</h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-md mx-auto">
              Private. Portable. Yours forever on Nostr.
            </p>
            <LoginArea className="mx-auto" />
          </div>
        ) : (
          <>
            <div className="mb-8">
              <h1 className="text-4xl font-bold tracking-tight">Training &amp; Body Tracker</h1>
              <p className="text-muted-foreground">Logged in as {user.pubkey.slice(0, 8)}…</p>
            </div>

            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="space-y-6">
              <TabsList className="grid w-full grid-cols-3 max-w-md">
                <TabsTrigger value="training">Training</TabsTrigger>
                <TabsTrigger value="body">Body Tracking</TabsTrigger>
                <TabsTrigger value="nutrition">Nutrition</TabsTrigger>
              </TabsList>

              {/* TRAINING SECTION - CLEAN REBUILD */}
              <TabsContent value="training" className="space-y-8">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <div>
                    <h2 className="text-2xl font-semibold">Training Plans</h2>
                    <p className="text-muted-foreground">Create, edit, and review your workout plans</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={exportTrainingPlans}>
                      Export
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => document.getElementById('import-training')?.click()}>
                      Import
                    </Button>
                    <input
                      id="import-training"
                      type="file"
                      accept=".json"
                      className="hidden"
                      onChange={importTrainingPlans}
                    />
                  </div>
                  <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
                    <DialogTrigger asChild>
                      <Button onClick={() => { setEditingPlan(null); resetPlanForm(); }}>
                        <Plus className="mr-2 h-4 w-4" /> New Plan
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-[95vw] max-w-[900px]">
                      <DialogHeader>
                        <DialogTitle>{editingPlan ? 'Edit' : 'Create'} Training Plan</DialogTitle>
                      </DialogHeader>

                      <div className="space-y-6 py-4">
                        {/* Step 1: Basic Information */}
                        <div>
                          <h3 className="font-semibold mb-3">1. Basic Information</h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <Label>Plan Name</Label>
                              <Input value={planForm.name} onChange={e => setPlanForm({ ...planForm, name: e.target.value })} placeholder="Push Pull Legs" />
                            </div>
                            <div>
                              <Label>Frequency</Label>
                              <Input value={planForm.frequency} onChange={e => setPlanForm({ ...planForm, frequency: e.target.value })} placeholder="3x per week" />
                            </div>
                            <div>
                              <Label>Goal</Label>
                              <Input value={planForm.goal} onChange={e => setPlanForm({ ...planForm, goal: e.target.value })} placeholder="Build muscle" />
                            </div>
                          </div>
                        </div>

                        {/* Step 2: Exercises */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold">2. Exercises</h3>
                            <Button variant="outline" size="sm" onClick={addExercise}>
                              <Plus className="h-4 w-4 mr-2" /> Add Exercise
                            </Button>
                          </div>

                          {/* Horizontal row of small squares for collapsed exercises */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            {planForm.exercises.map((ex, idx) => {
                              if (expandedExerciseIndex === idx) return null;
                              return (
                                <button
                                  key={idx}
                                  onClick={() => setExpandedExerciseIndex(idx)}
                                  className="w-9 h-9 flex items-center justify-center border rounded hover:bg-muted transition-colors text-sm font-semibold"
                                >
                                  #{idx + 1}
                                </button>
                              );
                            })}
                            <Button variant="outline" size="sm" onClick={addExercise}>
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>

                          {/* Currently expanded exercise (if any) */}
                          {planForm.exercises.map((ex, idx) => {
                            if (expandedExerciseIndex !== idx) return null;

                            return (
                              <div key={idx} className="border rounded-lg bg-muted/30 p-4 mb-3">
                                <div className="flex items-center gap-3 mb-4">
                                  <div className="font-semibold text-lg w-8">#{idx + 1}</div>
                                  <Input className="flex-1" placeholder="Exercise name" value={ex.name} onChange={e => {
                                    const exs = [...planForm.exercises]; exs[idx].name = e.target.value; setPlanForm({ ...planForm, exercises: exs });
                                  }} />
                                  <Button variant="ghost" size="icon" onClick={() => {
                                    const exs = planForm.exercises.filter((_, i) => i !== idx);
                                    setPlanForm({ ...planForm, exercises: exs.length ? exs : [{ name: '', sets: 3, reps: 10, weight: 0, rest: 60 }] });
                                    setExpandedExerciseIndex(null);
                                  }}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                  <div><Label className="text-xs">Sets</Label><Input type="number" value={ex.sets} onChange={e => { const exs = [...planForm.exercises]; exs[idx].sets = +e.target.value; setPlanForm({ ...planForm, exercises: exs }); }} /></div>
                                  <div><Label className="text-xs">Reps</Label><Input type="number" value={ex.reps} onChange={e => { const exs = [...planForm.exercises]; exs[idx].reps = +e.target.value; setPlanForm({ ...planForm, exercises: exs }); }} /></div>
                                  <div><Label className="text-xs">Weight (kg)</Label><Input type="number" value={ex.weight || ''} onChange={e => { const exs = [...planForm.exercises]; exs[idx].weight = +e.target.value; setPlanForm({ ...planForm, exercises: exs }); }} /></div>
                                  <div><Label className="text-xs">Rest (s)</Label><Input type="number" value={ex.rest || ''} onChange={e => { const exs = [...planForm.exercises]; exs[idx].rest = +e.target.value; setPlanForm({ ...planForm, exercises: exs }); }} /></div>
                                </div>

                                <div className="flex items-center gap-2">
                                  <Label className="text-xs">Position</Label>
                                  <Input type="number" className="w-16" value={idx + 1} min={1} max={planForm.exercises.length} onChange={(e) => {
                                    const newPos = parseInt(e.target.value);
                                    if (isNaN(newPos) || newPos < 1 || newPos > planForm.exercises.length) return;
                                    const exs = [...planForm.exercises];
                                    const [moved] = exs.splice(idx, 1);
                                    exs.splice(newPos - 1, 0, moved);
                                    setPlanForm({ ...planForm, exercises: exs });
                                  }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Step 3: Description */}
                        <div>
                          <h3 className="font-semibold mb-3">3. Description (optional)</h3>
                          <Input value={planForm.description} onChange={e => setPlanForm({ ...planForm, description: e.target.value })} placeholder="Any notes..." />
                        </div>

                        <Button onClick={saveTrainingPlan} className="w-full" size="lg">Save Training Plan</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                {/* Active Plans */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold">Active Plans</h3>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {activePlans.length === 0 && <p className="text-muted-foreground col-span-full">No active training plans yet.</p>}
                    {activePlans.map(plan => (
                      <Card key={plan.id}>
                        <CardHeader>
                          <CardTitle className="flex justify-between">
                            {plan.name}
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => editTrainingPlan(plan)}><Edit2 className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" onClick={() => archivePlan(plan.id)} title="Archive"><Archive className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" onClick={() => deleteTrainingPlan(plan.id)}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </CardTitle>
                          <CardDescription>{plan.goal} • {plan.frequency}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm mb-3">{plan.description}</p>
                          <div className="text-xs text-muted-foreground">{plan.exercises.length} exercises</div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Archived Plans */}
                {archivedPlans.length > 0 && (
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold mb-3 text-muted-foreground">Archived Plans</h3>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {archivedPlans.map(plan => (
                        <Card key={plan.id} className="opacity-75 border-dashed">
                          <CardHeader>
                            <CardTitle className="flex justify-between text-muted-foreground">
                              {plan.name}
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" onClick={() => editTrainingPlan(plan)}><Edit2 className="h-4 w-4" /></Button>
                                <Button variant="ghost" size="icon" onClick={() => unarchivePlan(plan.id)} title="Restore"><ArchiveRestore className="h-4 w-4" /></Button>
                                <Button variant="ghost" size="icon" onClick={() => deleteTrainingPlan(plan.id)}><Trash2 className="h-4 w-4" /></Button>
                              </div>
                            </CardTitle>
                            <CardDescription className="text-muted-foreground">{plan.goal} • {plan.frequency}</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <p className="text-sm mb-3">{plan.description}</p>
                            <div className="text-xs text-muted-foreground">{plan.exercises.length} exercises</div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* BODY TRACKING SECTION */}
              <TabsContent value="body" className="space-y-8">
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Entry Form */}
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <div>
                          <CardTitle>Log Body Metrics</CardTitle>
                          <CardDescription>Weight, body composition</CardDescription>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={exportBodyMetrics}>
                            Export
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => document.getElementById('import-body')?.click()}>
                            Import
                          </Button>
                          <input
                            id="import-body"
                            type="file"
                            accept=".json"
                            className="hidden"
                            onChange={importBodyMetrics}
                          />
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <Label>Date</Label>
                          <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={handleDateSelect}
                            modifiers={{ logged: loggedDates }}
                            modifiersClassNames={{ logged: 'bg-primary/20 font-bold text-primary' }}
                            className="rounded-md border"
                          />
                        </div>
                        <div className="flex-1 space-y-3">
                          {/* Weight, Muscle %, Body Fat %, Visceral Fat — new order */}
                          {[
                            { label: 'Weight (kg)', state: weightInput, setter: setWeightInput, field: 'weight', placeholder: '82.5' },
                            { label: 'Muscle %', state: musclePctInput, setter: setMusclePctInput, field: 'musclePct', placeholder: '40.0' },
                            { label: 'Body Fat %', state: bodyFatInput, setter: setBodyFatInput, field: 'bodyFat', placeholder: '15.0' },
                          ].map(({ label, state, setter, field, placeholder }) => (
                            <div key={field}>
                              <Label>{label}</Label>
                              <Input
                                type="text"
                                inputMode="decimal"
                                value={state}
                                onChange={(e) => {
                                  let val = e.target.value.replace(',', '.');
                                  if (/^\d*\.?\d{0,1}$/.test(val) || val === '') {
                                    setter(val);
                                    const num = parseFloat(val);
                                    if (!isNaN(num)) {
                                      setBodyForm({ ...bodyForm, [field]: Math.round(num * 10) / 10 });
                                    }
                                  }
                                }}
                                placeholder={placeholder}
                              />
                              <p className="text-xs text-muted-foreground mt-1">Use comma or dot (e.g. 15,2 or 15.2)</p>
                            </div>
                          ))}

                          {/* Visceral Fat */}
                          <div>
                            <Label>Visceral Fat %</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={bodyForm.visceralFat}
                              onChange={e => setBodyForm({ ...bodyForm, visceralFat: +e.target.value })}
                            />
                          </div>

                          {/* Action buttons */}
                          <div className="flex gap-2 pt-2">
                            <Button
                              variant="outline"
                              onClick={() => {
                                if (originalMetric) {
                                  // Undo: restore to last saved values
                                  setBodyForm({
                                    weight: originalMetric.weight,
                                    bodyFat: originalMetric.bodyFat,
                                    musclePct: Math.round(((originalMetric.muscle / originalMetric.weight) * 100) * 10) / 10,
                                    visceralFat: originalMetric.visceralFat,
                                  });
                                  setWeightInput(originalMetric.weight.toFixed(1));
                                  setBodyFatInput(originalMetric.bodyFat.toFixed(1));
                                  setMusclePctInput(((originalMetric.muscle / originalMetric.weight) * 100).toFixed(1));
                                } else {
                                  // Clear blank form
                                  setBodyForm({ weight: 0, bodyFat: 0, musclePct: 0, visceralFat: 0 });
                                  setWeightInput('');
                                  setBodyFatInput('');
                                  setMusclePctInput('');
                                }
                              }}
                            >
                              Undo latest
                            </Button>

                              <Button
                                variant="destructive"
                                onClick={() => {
                                  const dateStr = format(selectedDate, 'yyyy-MM-dd');
                                  // Remove entry for selected date
                                  setBodyMetrics(prev => prev.filter(m => m.date !== dateStr));
                                  setOriginalMetric(null);
                                  // Clear form
                                  setBodyForm({ weight: 0, bodyFat: 0, musclePct: 0, visceralFat: 0 });
                                  setWeightInput('');
                                  setBodyFatInput('');
                                  setMusclePctInput('');
                                  // Prevent query from restoring old data
                                  queryClient.setQueryData(['tracker-data', user?.pubkey], { plans: [], body: [], nutrition: [] });
                                }}
                              >
                                Clear date
                              </Button>

                            <Button
                              variant="destructive"
                              onClick={() => setShowClearAllDialog(true)}
                            >
                              Clear all
                            </Button>
                          </div>

                          {/* Clear All Confirmation Dialog */}
                          <Dialog open={showClearAllDialog} onOpenChange={setShowClearAllDialog}>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Clear all body metrics?</DialogTitle>
                              </DialogHeader>
                              <p className="text-sm text-muted-foreground">
                                This will permanently delete all logged body measurements. This action cannot be undone.
                              </p>
                              <div className="flex justify-end gap-2 pt-4">
                                <Button variant="outline" onClick={() => setShowClearAllDialog(false)}>
                                  Cancel
                                </Button>
                                <Button
                                  variant="destructive"
                                  onClick={() => {
                                    setBodyMetrics([]);
                                    setOriginalMetric(null);
                                    setBodyForm({ weight: 0, bodyFat: 0, musclePct: 0, visceralFat: 0 });
                                    setWeightInput('');
                                    setBodyFatInput('');
                                    setMusclePctInput('');
                                    // Prevent query from restoring old data
                                    queryClient.setQueryData(['tracker-data', user?.pubkey], { plans: [], body: [], nutrition: [] });
                                    setShowClearAllDialog(false);
                                  }}
                                >
                                  Yes, clear all data
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                          <Button onClick={saveBodyMetric} className="w-full">Save Measurement</Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Chart Controls */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Visualizations</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {[
                          { key: 'weight', label: 'Weight (kg)' },
                          { key: 'bodyFatPct', label: 'Body Fat %' },
                          { key: 'bodyFatKg', label: 'Body Fat (kg)' },
                          { key: 'muscleKg', label: 'Muscle (kg)' },
                          { key: 'musclePct', label: 'Muscle %' },
                          { key: 'visceral', label: 'Visceral Fat %' },
                        ].map(({ key, label }) => (
                          <Button
                            key={key}
                            variant={chartSeries.includes(key) ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => toggleSeries(key)}
                          >
                            {label}
                          </Button>
                        ))}
                      </div>

                      <div className="mb-4">
                        <Label>Date Range</Label>
                        <div className="flex gap-2 mt-1">
                          <Input type="date" value={format(dateRange.from, 'yyyy-MM-dd')} onChange={e => setDateRange({ ...dateRange, from: parseISO(e.target.value) })} />
                          <Input type="date" value={format(dateRange.to, 'yyyy-MM-dd')} onChange={e => setDateRange({ ...dateRange, to: parseISO(e.target.value) })} />
                        </div>
                      </div>

                      {/* Date Comparison Tool */}
                      <div className="mt-6 border-t pt-4">
                        <Label className="mb-2 block">Compare Two Dates</Label>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div>
                            <Label className="text-xs">From</Label>
                            <Input
                              type="date"
                              value={compareDate1 ? format(compareDate1, 'yyyy-MM-dd') : ''}
                              onChange={e => setCompareDate1(e.target.value ? parseISO(e.target.value) : null)}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">To</Label>
                            <Input
                              type="date"
                              value={compareDate2 ? format(compareDate2, 'yyyy-MM-dd') : ''}
                              onChange={e => setCompareDate2(e.target.value ? parseISO(e.target.value) : null)}
                            />
                          </div>
                        </div>

                        {compareDate1 && compareDate2 && (
                          <ComparisonResults
                            date1={compareDate1}
                            date2={compareDate2}
                            metrics={bodyMetrics}
                          />
                        )}
                      </div>

                      {filteredBodyData.length > 0 ? (
                        <div className="h-[320px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={filteredBodyData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="date" />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              {chartSeries.includes('weight') && <Line type="monotone" dataKey="weight" stroke="#8884d8" name="Weight (kg)" dot={false} />}
                              {chartSeries.includes('bodyFatPct') && <Line type="monotone" dataKey="bodyFatPct" stroke="#ff7300" name="Body Fat %" dot={false} />}
                              {chartSeries.includes('bodyFatKg') && <Line type="monotone" dataKey="bodyFatKg" stroke="#ffaa00" name="Body Fat (kg)" dot={false} />}
                              {chartSeries.includes('muscleKg') && <Line type="monotone" dataKey="muscleKg" stroke="#0088fe" name="Muscle (kg)" dot={false} />}
                              {chartSeries.includes('musclePct') && <Line type="monotone" dataKey="musclePct" stroke="#00aaff" name="Muscle %" dot={false} />}
                              {chartSeries.includes('visceral') && <Line type="monotone" dataKey="visceral" stroke="#ff0000" name="Visceral Fat %" dot={false} />}
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <p className="text-center text-muted-foreground py-12">No data in selected range. Log your first measurement!</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* NUTRITION SECTION */}
              <TabsContent value="nutrition">
                <div className="grid md:grid-cols-5 gap-6">
                  <Card className="md:col-span-2">
                    <CardHeader>
                      <div className="flex justify-between items-center flex-wrap gap-2">
                        <CardTitle>Log Nutrition</CardTitle>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={exportNutrition}>
                            Export
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => document.getElementById('import-nutrition')?.click()}>
                            Import
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setShowClearNutritionDialog(true)}
                          >
                            Clear all
                          </Button>
                          <input
                            id="import-nutrition"
                            type="file"
                            accept=".json"
                            className="hidden"
                            onChange={importNutrition}
                          />
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label>Date</Label>
                        <Calendar
                          mode="single"
                          selected={nutritionDate}
                          onSelect={d => d && setNutritionDate(d)}
                          modifiers={{ logged: nutritionLoggedDates }}
                          modifiersClassNames={{ logged: 'bg-primary/20 font-bold text-primary' }}
                          className="rounded-md border"
                        />
                      </div>
                      <div><Label>Food / Drink</Label><Input value={nutritionForm.food} onChange={e => setNutritionForm({ ...nutritionForm, food: e.target.value })} placeholder="Chicken breast" /></div>
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Amount</Label><Input type="number" value={nutritionForm.amount} onChange={e => setNutritionForm({ ...nutritionForm, amount: +e.target.value })} /></div>
                        <div><Label>Unit</Label>
                          <Select value={nutritionForm.unit} onValueChange={v => setNutritionForm({ ...nutritionForm, unit: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="g">grams (g)</SelectItem>
                              <SelectItem value="ml">millilitres (ml)</SelectItem>
                              <SelectItem value="unit">units</SelectItem>
                              <SelectItem value="tbsp">tbsp</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Button onClick={saveNutrition} className="w-full">Log Entry</Button>
                    </CardContent>
                  </Card>

                  <Card className="md:col-span-3">
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <CardTitle>Entries for {format(nutritionDate, 'MMMM dd, yyyy')}</CardTitle>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => clearNutritionForDay(nutritionDate)}
                        >
                          Clear day
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {nutritionForDate.length > 0 ? (
                        <ul className="space-y-2">
                          {nutritionForDate.map((entry, i) => {
                            // Find the real index in the full array for deletion
                            const realIndex = nutritionEntries.findIndex(
                              e => e.date === entry.date && 
                                   e.food === entry.food && 
                                   e.amount === entry.amount && 
                                   e.unit === entry.unit
                            );

                            return (
                              <li key={i} className="flex justify-between items-center border-b pb-2 group">
                                <div>
                                  <span>{entry.food}</span>
                                  <span className="text-muted-foreground ml-2">
                                    {entry.amount} {entry.unit}
                                  </span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={() => removeNutritionEntry(realIndex)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p className="text-muted-foreground">No entries for this day yet.</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Clear Nutrition Confirmation Dialog */}
                  <Dialog open={showClearNutritionDialog} onOpenChange={setShowClearNutritionDialog}>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Clear all nutrition entries?</DialogTitle>
                      </DialogHeader>
                      <p className="text-sm text-muted-foreground">
                        This will permanently delete all logged nutrition entries. This action cannot be undone.
                      </p>
                      <div className="flex justify-end gap-2 pt-4">
                        <Button variant="outline" onClick={() => setShowClearNutritionDialog(false)}>
                          Cancel
                        </Button>
                        <Button variant="destructive" onClick={clearAllNutrition}>
                          Yes, clear all nutrition data
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>
    </div>
  );
};

export default Index;
